import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useCollabId} from 'components/DSYS/CollabProvider';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {useCallback, useEffect, useRef, useState} from 'react';

import {HeadingSettings} from './commands';
import {GuidanceSetting, WordLimitSetting} from './components';
import {EnhancedHeadingNode} from './EnhancedHeadingNode';
import {setPersistedHeadingCollapsed} from './HeadingCollapsePersistence';
import {findHeadingNodeByHeadingId} from './HeadingNodeUtils';
import {useCanEdit} from './RoleContext';
import {WordCountDisplay} from './WordCountWatcher';

interface HeadingSettingsPopoverProps {
  headingId: string;
  onClose?: () => void;
  className?: string;
}

export const HeadingSettingsPopover = ({
  headingId,
  onClose,
  className,
}: HeadingSettingsPopoverProps) => {
  const [editor] = useLexicalComposerContext();
  const popoverRef = useRef<HTMLDivElement>(null);

  const canEdit = useCanEdit();
  const [settings, setSettings] = useState<HeadingSettings>({});

  const updateSettings = useCallback(() => {
    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);
      if (node instanceof EnhancedHeadingNode) {
        setSettings(node.getSettings());
      } else if (node) {
        setSettings({});
      }
    });
  }, [editor, headingId]);

  const handleUpdateSettings = useCallback(
    (updates: Partial<HeadingSettings>) => {
      if (!canEdit) return;

      editor.update(() => {
        const node = findHeadingNodeByHeadingId(editor, headingId);
        if (node instanceof EnhancedHeadingNode) {
          // Update the settings first
          node.updateSettings(updates);
          setSettings(node.getSettings());
        }
      });
    },
    [editor, headingId, canEdit]
  );

  const handleWordLimitChange = useCallback(
    (value: string) => {
      const wordLimit = value ? parseInt(value, 10) : undefined;
      handleUpdateSettings({wordLimit: wordLimit && wordLimit > 0 ? wordLimit : undefined});
    },
    [handleUpdateSettings]
  );

  const handleGuidanceChange = useCallback(
    (value: string) => {
      handleUpdateSettings({guidance: value || undefined});
    },
    [handleUpdateSettings]
  );

  // Handle click outside to close popover
  useOutsideClick(
    popoverRef,
    () => {
      onClose?.();
    },
    [],
    false,
    'mousedown'
  );

  // Update settings when component mounts or heading changes
  useEffect(() => {
    updateSettings();
  }, [updateSettings]);

  return (
    <Box className={className}>
      {/* Settings Panel */}
      <Box
        ref={popoverRef}
        position="absolute"
        top="100%"
        right="0"
        backgroundColor="Background Base"
        borderStyle="Solid"
        borderColor="Border Subtle"
        borderRadius="Small"
        padding="12"
        marginTop="4"
        style={{
          minWidth: '320px',
          maxWidth: '400px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          wordWrap: 'break-word',
          overflow: 'hidden',
        }}
      >
        <Stack gap="12">
          <WordLimitSetting
            settings={settings}
            canEdit={canEdit}
            headingId={headingId}
            onWordLimitChange={handleWordLimitChange}
          />

          <GuidanceSetting
            settings={settings}
            canEdit={canEdit}
            onGuidanceChange={handleGuidanceChange}
          />
        </Stack>
      </Box>
    </Box>
  );
};

/**
 * Simplified settings popover for read-only mode
 */
export const HeadingSettingsReadOnly = ({
  headingId,
  className,
}: {
  headingId: string;
  className?: string;
}) => {
  const [editor] = useLexicalComposerContext();
  const collabId = useCollabId();
  const [collapsed, setCollapsed] = useState(false);
  const [settings, setSettings] = useState<HeadingSettings>({});

  const updateState = useCallback(() => {
    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);
      if (node instanceof EnhancedHeadingNode) {
        setSettings(node.getSettings());
        setCollapsed(node.getCollapsed());
      }
    });
  }, [editor, headingId]);

  const handleToggleCollapse = useCallback(() => {
    editor.update(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);
      if (node instanceof EnhancedHeadingNode) {
        node.toggleCollapsed();
        const isNowCollapsed = node.getCollapsed();
        setPersistedHeadingCollapsed(collabId, headingId, isNowCollapsed);
        node.markDirty();
        setCollapsed(isNowCollapsed);
      }
    });
  }, [collabId, editor, headingId]);

  useState(() => {
    updateState();
  });

  return (
    <Box className={className}>
      <Button
        variant="borderless"
        size="small"
        onClick={handleToggleCollapse}
        withIconStart={
          <UIIcon
            src={collapsed ? ChevronRightLine : ChevronDownLine}
            size="small"
            color="subtle"
          />
        }
        title={collapsed ? 'Expand section' : 'Collapse section'}
        style={{padding: '2px 4px', minWidth: 'auto'}}
      >
        {collapsed ? 'Expand' : 'Collapse'}
      </Button>

      <Box display="inline-block" marginLeft="8">
        <WordCountDisplay headingId={headingId} />
      </Box>

      {settings.guidance && (
        <Box marginTop="4" padding="8" backgroundColor="Background Subtle" borderRadius="Small">
          <Text size="small" color="subtle">
            <strong>Guidance:</strong> {settings.guidance}
          </Text>
        </Box>
      )}
    </Box>
  );
};
